import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { DialogType } from '../../../@types';
import { LS_KEYS, PAYMENT_METHOD_FILTER_OPTIONS } from '../../../constants';
import { PAYMENT_GATEWAY } from '../../../constants/enums';
import { AdditionalQueryParams } from '../../../hooks/usePagination';
import { useMarketplace } from '../../../hooks/useMarketplace';
import usePersist from '../../../hooks/usePersist';
import { filterTotalCreditSchema, FilterTotalCreditData } from '../../../schema';
import { normalizePaymentMethodFilter } from '../../../utils';
import FilterDialogLayout from '../../layouts/FilterDialogLayout';
import FormSelect from '../../molecules/FormSelect';

const DEFAULT_FORM_DATA: FilterTotalCreditData = {
  paymentMethod: 'ALL',
};

type Props = DialogType & {
  changeQuery: (data: AdditionalQueryParams) => void;
};

const FilterTotalCredit = ({ closeModal, changeQuery }: Props) => {
  const { setPersistData, getPersistData, deletePersistData } = usePersist(false);
  const { pathname } = useLocation();
  const { config } = useMarketplace();
  const formDataKey = `${LS_KEYS.appFormFilter}-${pathname}`;
  const hasCryptoPaymentGateway = config?.marketplaceConfig?.paymentGateway === PAYMENT_GATEWAY.CRYPTO;

  const getSavedData = () => getPersistData<FilterTotalCreditData>(formDataKey) || DEFAULT_FORM_DATA;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FilterTotalCreditData>({
    resolver: zodResolver(filterTotalCreditSchema),
    defaultValues: getSavedData(),
  });

  const onFormSubmit = (data: FilterTotalCreditData) => {
    setPersistData<FilterTotalCreditData>(formDataKey, data);
    changeQuery(normalizePaymentMethodFilter(data, hasCryptoPaymentGateway));
    closeModal?.();
  };

  const handleResetFilter = () => {
    deletePersistData(formDataKey);
    reset(DEFAULT_FORM_DATA);
    changeQuery(normalizePaymentMethodFilter(DEFAULT_FORM_DATA, hasCryptoPaymentGateway));
    closeModal?.();
  };

  return (
    <FilterDialogLayout
      label='total credit'
      onClose={() => {
        closeModal?.();
      }}
      onSave={handleSubmit(onFormSubmit)}
      onReset={handleResetFilter}
    >
      {hasCryptoPaymentGateway && (
        <FormSelect
          name='paymentMethod'
          label='Payment method'
          control={control}
          options={PAYMENT_METHOD_FILTER_OPTIONS}
          error={errors.paymentMethod}
        />
      )}
    </FilterDialogLayout>
  );
};

export default FilterTotalCredit;
